import React, { useState } from "react";
import { ref, push, set } from "firebase/database";
import { database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { FileText, X, DollarSign, Calendar } from "lucide-react";

/**
 * SendInvoiceModal Component
 * 
 * Modal for company to send invoices to clients.
 * Saves invoices to Firebase RTDB at:
 * Companies/{companyName}/ClientInvoices/{clientId}/invoices/{invoiceId}
 */

const SendInvoiceModal = ({ open, onClose, client, companyName, adminInfo }) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [submitting, setSubmitting] = useState(false);

  // Set default due date to 30 days from now
  React.useEffect(() => {
    if (!dueDate) {
      const thirtyDaysLater = new Date();
      thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
      setDueDate(thirtyDaysLater.toISOString().split('T')[0]);
    }
  }, [dueDate]);

  if (!open || !client) return null;

  const clientName =
    client.cloudName || client.CloudName || client.name || client.id || "Client";
  const clientIdentifier =
    client.otherName || client.OtherName || client.id;
  const clientEmail = client.email || client.Email || "";

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clientIdentifier) {
      toast.error("Missing client identifier. Please try again.");
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!description.trim()) {
      toast.error("Please enter invoice description");
      return;
    }

    if (!dueDate) {
      toast.error("Please select due date");
      return;
    }

    setSubmitting(true);

    try {
      // Generate invoice number
      const timestamp = Date.now();
      const prefix = companyName
        ? companyName.slice(0, 3).toUpperCase()
        : "UNK";
      const invoiceNumber = `INV-${prefix}-${timestamp}`;

      // Create invoice object
      const invoiceData = {
        invoiceNumber,
        invoiceDate,
        dueDate,
        amount: parseFloat(amount),
        description: description.trim(),
        status: "unpaid",
        companyName,
        clientId: clientIdentifier,
        clientName: clientName,
        clientEmail,
        createdAt: new Date().toISOString(),
        createdBy: adminInfo?.displayName || adminInfo?.email || "Company Admin",
      };

      // Save to Firebase RTDB
      const invoiceRef = ref(
        database,
        `Companies/${companyName}/ClientInvoices/${clientIdentifier}/invoices`
      );

      const newInvoiceRef = push(invoiceRef);
      await set(newInvoiceRef, invoiceData);

      toast.success(`Invoice ${invoiceNumber} sent to ${clientName}!`);
      onClose();

      // Reset form
      setAmount("");
      setDescription("");
      setDueDate("");
      setInvoiceDate(new Date().toISOString().split('T')[0]);
    } catch (error) {
      console.error("Error sending invoice:", error);
      toast.error("Failed to send invoice. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FileText className="w-6 h-6 text-cyan-600" />
            Send Invoice
          </h2>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Client Info */}
        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-cyan-800 mb-1">Sending invoice to:</p>
          <p className="text-lg font-bold text-cyan-900">{clientName}</p>
          <p className="text-sm text-cyan-700">
            Client ID: {clientIdentifier}
          </p>
          {clientEmail && (
            <p className="text-sm text-cyan-700">
              Email: {clientEmail}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Amount (KES) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g., 5000"
              min="0"
              step="0.01"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              disabled={submitting}
              required
            />
          </div>

          {/* Invoice Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Invoice Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              disabled={submitting}
              required
            />
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Due Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              disabled={submitting}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description/Items <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this invoice is for (e.g., 'Milk delivery for November 2024 - 500 liters')"
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              disabled={submitting}
              required
            />
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> The invoice will be sent to the client and they will be 
              able to view it from their dashboard. You can track payment status from the 
              client transactions tab.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  Send Invoice
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendInvoiceModal;
